import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { LoadingButton } from '../buttons/LoadingButton';
import ButtonGroup from '@mui/material/ButtonGroup';
import Typography from '@mui/material/Typography';
import { Game } from 'functions/src/types/firestore/Game';
import { useRouter } from 'next/router';
import { MintNowIcon } from '../icons/MintNowIcon';
import { PlayGameIcon } from '../icons/PlayGameIcon';
import { Link } from '../Link';
import { toKebabCase } from '../../util/string';
import { useState, useRef, useEffect } from 'react';
import { ComingSoonText } from '../coming-soon/ComingSoonText';
import { DialogCentered } from '../DialogCentered';

// TODO: make dynamically provided from backend
const MINT_NOW_OPTIONS = {
  'undead blocks': 'https://www.undeadblocks.com/mint-weapons',
  'fantom survivor': `/game/fantom-survivor/survivors`,
  metaderby: '/game/metaderby/',
};

export type VideoBannerButtonsProps = Pick<Game, 'title'> & {
  handlePlayGame: () => void;
};

export function VideoBannerButtons({
  title,
  handlePlayGame,
}: VideoBannerButtonsProps) {
  const theme = useTheme();
  const router = useRouter();
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [buttonOffset, setButtonOffset] = useState<number>();
  const buttonWidthRef = useRef<HTMLDivElement>(null);
  // TODO: Remove
  const isEnabled = !['mobile-legends', 'metaderby'].includes(
    toKebabCase(title),
  );
  const navigate = async () => {
    if (!isEnabled) {
      setComingSoonOpen(true);
      return;
    }
    if (title !== 'BluMint') {
      await router.push(`/game/${toKebabCase(title)}`);
    }
  };

  useEffect(() => {
    if (buttonWidthRef.current) {
      const offset = buttonWidthRef.current.offsetWidth * 2;
      setButtonOffset(offset);
    }
  }, [buttonWidthRef.current?.offsetWidth]);

  const boxShadow = theme.shadowsHard.primaryElevation2;

  const mintNowHref = MINT_NOW_OPTIONS[`${title.toLowerCase()}`];
  const canPlayNow = title !== 'BluMint';
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        position: 'absolute',
        bottom: 0,
        '& button': {
          mx: 1,
        },
        flexGrow: 1,
      }}
    >
      <ButtonGroup
        sx={{
          marginBottom: '8px',
          px: 1,
          width: 'calc(100% - 400px)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: '36px',
            flex: 1.5,
            width: '100%',
            minWidth: '100px',
            maxWidth: '260px',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              cursor: 'pointer',
              '&::before': {
                background: '#0053D8',
              },
              '&::after': {
                background:
                  'linear-gradient(270deg, #3CB7EE -25%, #0053D8 100%)',
              },
            },
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              top: '0',
              height: '100%',
              width: '55%',
              zIndex: -1,
              border: 'none',
            },
            '&::before': {
              left: 0,
              borderRadius: '5px',
              borderRight: 'none',
              transform: 'skew(-46deg)',
              transformOrigin: 'bottom left',
              background: '#0064E9',
              boxShadow,
            },
            '&::after': {
              right: 0,
              borderTopRightRadius: '5px',
              borderBottomRightRadius: '5px',
              borderLeft: 'none',
              background: 'linear-gradient(270deg, #4DC8FF -25%, #0064E9 100%)',
              boxShadow,
            },
          }}
          // onClick={() => handleNavigate(`game/${game.id}`)}
        >
          {mintNowHref && (
            <Link
              href={mintNowHref}
              sx={{ textDecoration: 'none', width: '100%' }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: 'transparent',
                  background: 'none',
                  border: 'none',
                  boxShadow: 0,
                  pointerEvents: 'none',
                  width: '100%',
                }}
                startIcon={
                  buttonOffset && buttonOffset <= 360 ? null : (
                    <MintNowIcon sx={{ mx: 2, height: 24, width: 24 }} />
                  )
                }
              >
                <Typography
                  variant={buttonOffset && buttonOffset <= 360 ? 'body1' : 'h6'}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 600,
                  }}
                >
                  Mint Now
                </Typography>
              </Button>
            </Link>
          )}
        </Box>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 2,
            width: `calc(100% - ${buttonOffset}px)`,
            minWidth: '100px',
          }}
        >
          <LoadingButton
            variant="contained"
            sx={{
              background: 'linear-gradient(0deg, #0064E9 -100%, #4DC8FF 100%)',
              height: '36px',
              width: '100%',
              '&:hover': {
                cursor: 'pointer',
                filter: 'brightness(100%)',
                background:
                  'linear-gradient(0deg, #0053D8 -100%, #3CB7EE 100%) !important',
              },
              marginLeft: '8px !important',
              marginRight: '8px !important',
              boxShadow,
            }}
            onClick={navigate}
          >
            <Typography
              variant="h6"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                maxWidth: '220px',
                fontWeight: 600,
              }}
            >
              {title}
            </Typography>
          </LoadingButton>
        </Box>
        <Box
          ref={buttonWidthRef}
          sx={{
            position: 'relative',
            height: '36px',
            flex: 1.5,
            width: '100%',
            minWidth: '100px',
            maxWidth: '260px',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              cursor: 'pointer',
              '&::after': {
                background:
                  'linear-gradient(90deg, #3CB7EE -25%, #0053D8 100%)',
              },
              '&::before': {
                background: '#0053D8',
              },
            },
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              top: '0',
              height: '100%',
              width: '55%',
              background: 'linear-gradient(0deg, #0064E9 16.67%, #4DC8FF 100%)',
              zIndex: -1,
              border: 'none',
            },
            '&::before': {
              right: 0,
              borderRadius: '5px',
              borderLeft: 'none',
              transform: 'skew(46deg)',
              transformOrigin: 'bottom right',
              background: '#0064E9',
              boxShadow,
            },
            '&::after': {
              left: 0,
              borderTopLeftRadius: '5px',
              borderBottomLeftRadius: '5px',
              borderRight: 'none',
              background: 'linear-gradient(90deg, #4DC8FF -25%, #0064E9 100%)',
              boxShadow,
            },
          }}
          onClick={handlePlayGame}
        >
          {canPlayNow && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: 'transparent',
                background: 'none',
                border: 'none',
                boxShadow: 0,
                pointerEvents: 'none',
                width: '100%',
              }}
              endIcon={
                buttonOffset && buttonOffset <= 360 ? null : (
                  <PlayGameIcon sx={{ mx: 2, height: 24, width: 24 }} />
                )
              }
            >
              <Typography
                variant={buttonOffset && buttonOffset <= 360 ? 'body1' : 'h6'}
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 600,
                }}
              >
                Play Game
              </Typography>
            </Button>
          )}
        </Box>
      </ButtonGroup>
      <DialogCentered
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        showCloseIcon
      >
        <ComingSoonText title={title} />
      </DialogCentered>
    </Box>
  );
}
